import { computed, Injectable } from '@angular/core';
import { freeze, isNullOrUndefined } from '@maplecroft-typescript/utils';
import { immutableSignal, ImmutableWritableSignal } from '@maplecroft-ui/ng-utils';
import { DEFAULT_SCORE_COLORS, DEFAULT_SUBNATIONAL_MAP_SCORE_COLORS } from './default-score-colors';
import { generateLinearColourRamp, hexToRgb, rgbToHex } from './helpers';
import { ColorRange, RGB, ScoreColor } from './models';

@Injectable({
  providedIn: 'root',
})
export class ScoreColorService {
  /**
   * The current selected color range. (Signal used to allow for dynamic color ranges in the future).
   */
  public readonly scoreColors: ImmutableWritableSignal<ScoreColor[]> = immutableSignal(DEFAULT_SCORE_COLORS);

  /**
   * The current selected subnational map color range - used for subnational rasters. (Signal used to allow for dynamic color ranges in the future).
   */
  public readonly subnationalMapScoreColors: ImmutableWritableSignal<ScoreColor[]> = immutableSignal(DEFAULT_SUBNATIONAL_MAP_SCORE_COLORS);

  public readonly scoreColorsWithoutNoData = computed(() => freeze(this.scoreColors().slice(1)));
  public readonly subnationalMapScoreColorsWithoutNoData = computed(() => freeze(this.subnationalMapScoreColors().slice(1)));

  public readonly colorRanges = computed(() => this.createColorRange(this.scoreColorsWithoutNoData()));
  public readonly subnationalMapColorRanges = computed(() => this.createColorRange(this.subnationalMapScoreColorsWithoutNoData()));

  /**
   * Converts ScoreColors to ColorRanges by adding minScore, maxScore, and gradient properties.
   */
  private createColorRange(scoreColors: readonly ScoreColor[]): readonly ColorRange[] {
    const totalRange = 10;
    const rangeSize = totalRange / scoreColors.length;

    const ramp = generateLinearColourRamp(scoreColors.map(({ color }) => hexToRgb(color)));

    return freeze(
      scoreColors.map((scoreColor, index) => {
        const maxScore = (index + 1) * rangeSize;

        return {
          ...scoreColor,
          // eslint-disable-next-line no-mixed-operators
          minScore: index === 0 ? 0 : index * rangeSize + 0.01,
          maxScore,
          gradient: `linear-gradient(to right, ${rgbToHex(this.interpolateScoreColors(index * rangeSize, 0, totalRange, ramp))} 0%, ${rgbToHex(this.interpolateScoreColors(maxScore, 0, totalRange, ramp))} 100%)`,
        };
      }),
    );
  }

  /**
   * Gets a ScoreColor based on the score and the maximum value.
   */
  public getScoreColor(score: number | null | undefined): Readonly<ScoreColor> {
    if (isNullOrUndefined(score)) {
      return this.scoreColors()[0];
    }

    return this.scoreColorsWithoutNoData()[this.colorRanges().findIndex(({ maxScore }) => score <= maxScore)];
  }

  /**
   * Gets an interpolated hex color based on the score and the maximum value.
   */
  public readonly rgbColorRanges = computed(() => freeze(this.scoreColorsWithoutNoData().map(({ color }) => hexToRgb(color))));

  /**
   * Gets a color in RGB format based on the score
   */
  public getRgbScoreColor(score: number | null | undefined): Readonly<RGB> {
    const { color } = this.getScoreColor(score);

    return hexToRgb(color);
  }

  /**
   * Subnational map color ranges in RGB format.
   */
  public readonly rbgSubnationalMapColorRanges = computed(() =>
    freeze(this.subnationalMapScoreColorsWithoutNoData().map(({ color }) => hexToRgb(color))),
  );

  /**
   * Gets an interpolated RGB subnational map color based on the score.
   */
  public getInterpolatedMapScoreColor(score: number | null | undefined, min = 0, max = 10): Readonly<RGB> {
    return freeze(this.interpolateScoreColors(score, min, max, this.subnationalMapScoreColorRamp()));
  }

  private interpolateScoreColors(score: number | null | undefined, min: number, max: number, ramp: ReadonlyMap<number, RGB>): RGB {
    if (isNullOrUndefined(score)) {
      return hexToRgb(this.scoreColors()[0].color);
    }

    // Score is between min and max. Convert score to a value between 1 and 255.
    const value = Math.round(((score - min) / (max - min)) * 254) + 1;

    return ramp.get(value) ?? [0, 0, 0];
  }

  public readonly subnationalMapScoreColorRamp = computed<ReadonlyMap<number, RGB>>(() => {
    const colorRanges = this.rbgSubnationalMapColorRanges();

    return freeze(generateLinearColourRamp(colorRanges));
  });

  public readonly riskLevelColorLookup = computed<Record<ScoreColor['riskLevel'], string>>(() =>
    freeze(Object.fromEntries(this.scoreColors().map(({ riskLevel, color }) => [riskLevel, color]))),
  );
}
